import React, { createContext, useContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { UploadProgressProvider, GlobalUploadProgress, useUploadProgress } from './contexts/UploadProgressContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import InvoiceList from './pages/InvoiceList';
import InvoiceUpload from './pages/InvoiceUpload';
import ApiUses from './pages/ApiUses';
import RuleManagement from './pages/RuleManagement';
import ChartView from './pages/ChartView';
import AliasManagement from './pages/AliasManagement';
import ActivityLogs from './pages/ActivityLogs';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

const UploadSectionVisitedContext = createContext();

export const useUploadSectionVisited = () => {
  const context = useContext(UploadSectionVisitedContext);
  if (!context) {
    throw new Error('useUploadSectionVisited must be used within an UploadSectionVisitedProvider');
  }
  return context;
};

export const UploadSectionVisitedProvider = ({ children }) => {
  const [hasVisitedUpload, setHasVisitedUpload] = useState(false);

  const markVisited = () => {
    setHasVisitedUpload(true);
  };

  return (
    <UploadSectionVisitedContext.Provider value={{ hasVisitedUpload, markVisited }}>
      {children}
    </UploadSectionVisitedContext.Provider>
  );
};

// Custom theme configuration for the application
const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f8f9fa',
    },
    text: {
      primary: '#212529',
      secondary: '#495057',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default MUI background gradient
        }
      }
    }
  }
});

/**
 * Component to handle route detection and upload progress display
 * Shows upload progress indicator when there are ongoing uploads
 */
const UploadProgressHandler = () => {
  const location = useLocation();
  const { uploads } = useUploadProgress();
  const { hasVisitedUpload, markVisited } = useUploadSectionVisited();
  const [hasNavigatedAway, setHasNavigatedAway] = useState(false);
  
  // Check if there are any ongoing uploads (pending, uploading, or processing)
  const hasOngoingUploads = Object.keys(uploads).some(key => 
    uploads[key].status === 'pending' || uploads[key].status === 'uploading' || uploads[key].status === 'processing'
  );
  
  // Check if user is on upload page
  const isOnUploadPage = location.pathname === '/upload' || location.pathname === '/invoice-upload';
  
  // Mark as visited when user visits the upload page
  React.useEffect(() => {
    if (isOnUploadPage) {
      markVisited();
    } else if (hasVisitedUpload && !isOnUploadPage && !hasNavigatedAway) {
      // User has visited upload page before and is now navigating away for the first time
      setHasNavigatedAway(true);
    }
    
    // Reset navigation state if user returns to upload page and there are no ongoing uploads
    if (isOnUploadPage && !hasOngoingUploads && hasNavigatedAway) {
      setHasNavigatedAway(false);
    }
  }, [location.pathname, isOnUploadPage, hasVisitedUpload, hasNavigatedAway, hasOngoingUploads, markVisited]);
  
  // Show the global progress indicator if there are ongoing uploads
  // Show only on non-upload pages initially, but after first navigation away, show on all pages
  const shouldShow = hasOngoingUploads && (!isOnUploadPage || hasNavigatedAway);
  
  return <GlobalUploadProgress shouldShow={shouldShow} />;
};

/**
 * Main application component that sets up routing and global providers
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorProvider>
        <AuthProvider>
          <UploadProgressProvider>
            <UploadSectionVisitedProvider>
              <DashboardProvider>
                <Router>
                  <div className="App">
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route 
                        path="/" 
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <Dashboard />
                            </Layout>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/invoices" 
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <InvoiceList />
                            </Layout>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/upload" 
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <InvoiceUpload />
                            </Layout>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/api-uses" 
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <ApiUses />
                            </Layout>
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/rules" 
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <RuleManagement />
                            </Layout>
                          </ProtectedRoute>
                        } 
                      />
                    <Route 
                      path="/charts" 
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ChartView />
                          </Layout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/alias-management" 
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AliasManagement />
                          </Layout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/activity-logs" 
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ActivityLogs />
                          </Layout>
                        </ProtectedRoute>
                      } 
                    />
                  </Routes>
                  {/* Render the upload progress indicator outside of the routes */}
                  <UploadProgressHandler />
                </div>
              </Router>
            </DashboardProvider>
          </UploadSectionVisitedProvider>
        </UploadProgressProvider>
      </AuthProvider>
    </ErrorProvider>
  </ThemeProvider>
);
}

export default App;